import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import socket from "../views/pages/Sales1/socket";

/**
 * useRecordLock — generic record locking hook.
 *
 * Usage:
 *   const { isReadOnly, lockedBy, requestAccess } = useRecordLock("leads", id);
 *
 * - On mount: tries to acquire lock via Socket.IO
 * - On unmount / tab close: releases the lock
 * - If another user holds the lock, returns isReadOnly=true with lockedBy info
 */
export function useRecordLock(recordType, recordId) {
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const userId = loginUser?.id;
  const userName = loginUser ? `${loginUser.firstname} ${loginUser.lastname}` : "";

  const [isReadOnly, setIsReadOnly] = useState(false);
  const [lockedBy, setLockedBy] = useState(null); // { userId, userName }
  const [lockChecked, setLockChecked] = useState(false);
  const [releaseRequested, setReleaseRequested] = useState(false);
  const [releaseRequestFrom, setReleaseRequestFrom] = useState(null);

  const recordRef = useRef({ recordType, recordId });
  recordRef.current = { recordType, recordId };

  const emitUnlock = useCallback(() => {
    if (recordRef.current.recordId && userId) {
      socket.emit("record:unlock", {
        recordType: recordRef.current.recordType,
        recordId: recordRef.current.recordId,
        userId,
      });
    }
  }, [userId]);

  const requestAccess = useCallback(() => {
    if (recordId && userName) {
      socket.emit("record:force-request", {
        recordType,
        recordId,
        requestedBy: { userId, userName },
      });
      setReleaseRequested(true);
    }
  }, [recordType, recordId, userId, userName]);

  const dismissReleaseRequest = useCallback(() => {
    setReleaseRequestFrom(null);
  }, []);

  const releaseForRequester = useCallback(() => {
    emitUnlock();
    setReleaseRequestFrom(null);
  }, [emitUnlock]);

  useEffect(() => {
    if (!recordId || !userId) return;

    // --- Register ALL listeners FIRST (before emitting) ---
    const handleLocked = (data) => {
      if (data.recordType === recordType && data.recordId === recordId) {
        setIsReadOnly(false);
        setLockedBy(null);
        setLockChecked(true);
      }
    };

    const handleAlreadyLocked = (data) => {
      if (data.recordType === recordType && data.recordId === recordId) {
        setIsReadOnly(true);
        setLockedBy(data.lockedBy);
        setLockChecked(true);
      }
    };

    const handleReleased = (data) => {
      if (data.recordType === recordType && data.recordId === recordId) {
        // Lock was released — try to acquire it now
        setIsReadOnly(false);
        setLockedBy(null);
        setReleaseRequested(false);
        socket.emit("record:lock", { recordType, recordId, userId, userName });
      }
    };

    const handleReleaseRequest = (data) => {
      if (data.recordType === recordType && data.recordId === recordId) {
        setReleaseRequestFrom(data.requestedBy);
      }
    };

    socket.on("record:locked", handleLocked);
    socket.on("record:already-locked", handleAlreadyLocked);
    socket.on("record:released", handleReleased);
    socket.on("record:release-request", handleReleaseRequest);

    // --- NOW emit the lock request ---
    const emitLockRequest = () => {
      socket.emit("record:lock", { recordType, recordId, userId, userName });
    };

    if (socket.connected) {
      emitLockRequest();
    }

    // Also emit on (re)connect so locks work after reconnection
    socket.on("connect", emitLockRequest);

    // Cleanup: remove listeners and unlock on unmount
    return () => {
      socket.off("record:locked", handleLocked);
      socket.off("record:already-locked", handleAlreadyLocked);
      socket.off("record:released", handleReleased);
      socket.off("record:release-request", handleReleaseRequest);
      socket.off("connect", emitLockRequest);
      emitUnlock();
    };
  }, [recordType, recordId, userId, userName, emitUnlock]);

  // Handle browser/tab close
  useEffect(() => {
    const handleBeforeUnload = () => emitUnlock();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [emitUnlock]);

  return {
    isReadOnly,
    lockedBy,
    lockChecked,
    requestAccess,
    releaseRequested,
    releaseRequestFrom,
    dismissReleaseRequest,
    releaseForRequester,
  };
}
